import { db } from '../firebase/config.ts';
import {
  collection,
  getDocs,
  getCountFromServer,
  query,
  where,
  limit,
  orderBy,
  GeoPoint,
} from 'firebase/firestore';

export interface HostDoc {
  id: string;
  title: string;
  host_type?: string;
  url?: string;
  website_url?: string;
  websiteUrl?: string;
  cover_Image?: string;
  logo_Image?: string;
  short_description?: string;
  description?: string;
  aliases?: string[];
  latLong?: GeoPoint | null;
  followers?: string[];
  likes?: number;
  eventCount?: number;
  userId?: string;
  updatedAt?: string;
  createdAt?: string;
  profile_source?: string;
}

export interface HostWithStats extends HostDoc {
  linkedPostCount: number;
  officialUrl: string;
  hasGeo: boolean;
  hasCover: boolean;
  hasDescription: boolean;
  coverStatus: 'ok' | 'svg' | 'missing';
}

export interface HostAuditSummary {
  totalHosts: number;
  missingGeo: number;
  missingCover: number;
  svgCover: number;
  missingDescription: number;
  missingUrl: number;
}

function classifyCover(url?: string): 'ok' | 'svg' | 'missing' {
  const raw = (url || '').trim();
  if (!raw) return 'missing';
  const lower = raw.toLowerCase().split('?')[0];
  if (lower.endsWith('.svg')) return 'svg';
  if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('//')) return 'ok';
  return 'missing';
}

function hasValidGeo(latLong: any): boolean {
  if (!latLong) return false;
  if (latLong instanceof GeoPoint) return true;
  if (typeof latLong === 'object' && latLong.latitude != null && latLong.longitude != null) return true;
  return false;
}

function pickOfficialUrl(host: HostDoc): string {
  return (host.website_url || host.websiteUrl || host.url || '').trim();
}

export const hostService = {
  async getAllHosts(): Promise<HostDoc[]> {
    const hostsRef = collection(db, 'Hosts');
    const snapshot = await getDocs(hostsRef);
    return snapshot.docs.map((doc) => ({
      ...(doc.data() as Omit<HostDoc, 'id'>),
      id: doc.id,
    }));
  },

  /**
   * Per-host post counts using aggregation queries only (no full document reads).
   * The previous implementation used getDocs on hostID-in batches and downloaded every
   * matching post, which could hang the browser on large datasets.
   */
  async getLinkedPostCounts(hostIds: string[]): Promise<Record<string, number>> {
    const ids = [...new Set(hostIds.filter(Boolean))];
    if (ids.length === 0) return {};

    const postsRef = collection(db, 'posts');
    const counts: Record<string, number> = {};
    const parallel = 20;

    for (let i = 0; i < ids.length; i += parallel) {
      const chunk = ids.slice(i, i + parallel);
      const results = await Promise.all(
        chunk.map(async (hid) => {
          const q = query(postsRef, where('hostID', '==', hid));
          const snap = await getCountFromServer(q);
          return [hid, snap.data().count] as const;
        }),
      );
      for (const [hid, c] of results) {
        counts[hid] = c;
      }
    }
    return counts;
  },

  async getUnmappedPlaceCandidates(
    hostTitles: Set<string>,
    sampleSize: number = 500,
  ): Promise<{ place: string; count: number; source: string; sampleEventUrl: string }[]> {
    const postsRef = collection(db, 'posts');
    let snapshot;
    try {
      const q = query(postsRef, orderBy('createdAt', 'desc'), limit(sampleSize));
      snapshot = await getDocs(q);
    } catch {
      const q = query(postsRef, limit(sampleSize));
      snapshot = await getDocs(q);
    }

    const placeCounts = new Map<string, { count: number; source: string; eventUrl: string }>();
    const normalizedTitles = new Set(
      Array.from(hostTitles).map((t) => t.toLowerCase().trim()),
    );

    snapshot.docs.forEach((doc) => {
      const data = doc.data();
      if (data.hostID) return;
      const place = (data.place || '').trim();
      if (!place) return;
      if (normalizedTitles.has(place.toLowerCase().trim())) return;

      const existing = placeCounts.get(place);
      if (existing) {
        existing.count++;
      } else {
        placeCounts.set(place, {
          count: 1,
          source: data.source || '',
          eventUrl: data.event_url || '',
        });
      }
    });

    return Array.from(placeCounts.entries())
      .map(([place, info]) => ({ place, ...info, sampleEventUrl: info.eventUrl }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 30);
  },

  enrichHostsWithStats(hosts: HostDoc[], postCounts: Record<string, number>): HostWithStats[] {
    return hosts.map((host) => ({
      ...host,
      linkedPostCount: postCounts[host.id] || 0,
      officialUrl: pickOfficialUrl(host),
      hasGeo: hasValidGeo(host.latLong),
      hasCover: classifyCover(host.cover_Image) === 'ok',
      hasDescription: (host.short_description || host.description || '').trim().length >= 40,
      coverStatus: classifyCover(host.cover_Image),
    }));
  },

  computeAuditSummary(hosts: HostWithStats[]): HostAuditSummary {
    return {
      totalHosts: hosts.length,
      missingGeo: hosts.filter((h) => !h.hasGeo).length,
      missingCover: hosts.filter((h) => h.coverStatus === 'missing').length,
      svgCover: hosts.filter((h) => h.coverStatus === 'svg').length,
      missingDescription: hosts.filter((h) => !h.hasDescription).length,
      missingUrl: hosts.filter((h) => !h.officialUrl).length,
    };
  },
};

export default hostService;
